/**
 * Acquisition Tax Rates (Grunderwerbsteuer-Sätze) for German States
 * 
 * These rates are used to calculate acquisition tax on property purchases.
 * The values represent the tax rate percentage per state.
 * 
 * Data structure is designed to be easily migrated to a database table.
 * 
 * Table schema for future database:
 * CREATE TABLE acquisition_tax_rates (
 *   id INT PRIMARY KEY AUTO_INCREMENT,
 *   state_name VARCHAR(50) UNIQUE NOT NULL,
 *   state_code VARCHAR(5) UNIQUE NOT NULL,
 *   tax_rate DECIMAL(5,2) NOT NULL,
 *   description VARCHAR(255),
 *   last_updated DATE,
 *   created_at TIMESTAMP DEFAULT CURRENT_TIMESTAMP
 * );
 */

export interface AcquisitionTaxRate {
  stateName: string;
  stateCode: string;
  taxRate: number;
  description?: string;
}

export const ACQUISITION_TAX_RATES: AcquisitionTaxRate[] = [
  {
    stateName: 'Baden-Württemberg',
    stateCode: 'BW',
    taxRate: 5.0,
    description: 'Acquisition tax rate in Baden-Württemberg'
  },
  {
    stateName: 'Bayern',
    stateCode: 'BY',
    taxRate: 3.5,
    description: 'Acquisition tax rate in Bavaria'
  },
  {
    stateName: 'Berlin',
    stateCode: 'BE',
    taxRate: 6.0,
    description: 'Acquisition tax rate in Berlin'
  },
  {
    stateName: 'Brandenburg',
    stateCode: 'BB',
    taxRate: 6.5,
    description: 'Acquisition tax rate in Brandenburg'
  },
  {
    stateName: 'Bremen',
    stateCode: 'HB',
    taxRate: 5.0,
    description: 'Acquisition tax rate in Bremen'
  },
  {
    stateName: 'Hamburg',
    stateCode: 'HH',
    taxRate: 5.5,
    description: 'Acquisition tax rate in Hamburg'
  },
  {
    stateName: 'Hessen',
    stateCode: 'HE',
    taxRate: 6.0,
    description: 'Acquisition tax rate in Hesse'
  },
  {
    stateName: 'Mecklenburg-Vorpommern',
    stateCode: 'MV',
    taxRate: 6.0,
    description: 'Acquisition tax rate in Mecklenburg-Vorpommern'
  },
  {
    stateName: 'Niedersachsen',
    stateCode: 'NI',
    taxRate: 5.0,
    description: 'Acquisition tax rate in Lower Saxony'
  },
  {
    stateName: 'Nordrhein-Westfalen',
    stateCode: 'NW',
    taxRate: 6.5,
    description: 'Acquisition tax rate in North Rhine-Westphalia'
  },
  {
    stateName: 'Rheinland-Pfalz',
    stateCode: 'RP',
    taxRate: 5.0,
    description: 'Acquisition tax rate in Rhineland-Palatinate'
  },
  {
    stateName: 'Saarland',
    stateCode: 'SL',
    taxRate: 6.5,
    description: 'Acquisition tax rate in Saarland'
  },
  {
    stateName: 'Sachsen',
    stateCode: 'SN',
    taxRate: 5.5,
    description: 'Acquisition tax rate in Saxony'
  },
  {
    stateName: 'Sachsen-Anhalt',
    stateCode: 'ST',
    taxRate: 5.0,
    description: 'Acquisition tax rate in Saxony-Anhalt'
  },
  {
    stateName: 'Schleswig-Holstein',
    stateCode: 'SH',
    taxRate: 6.5,
    description: 'Acquisition tax rate in Schleswig-Holstein'
  },
  {
    stateName: 'Thüringen',
    stateCode: 'TH',
    taxRate: 5.0,
    description: 'Acquisition tax rate in Thuringia'
  }
];

/**
 * Helper function to get tax rate by state name
 * Can be easily replaced with a database query later
 */
export function getAcquisitionTaxRateByState(stateName: string): number | null {
  const rate = ACQUISITION_TAX_RATES.find(
    r => r.stateName.toLowerCase() === stateName.toLowerCase()
  );
  return rate?.taxRate ?? null;
}

/**
 * Helper function to get state code by name
 */
export function getStateCodeAcquisition(stateName: string): string | null {
  const state = ACQUISITION_TAX_RATES.find(
    r => r.stateName.toLowerCase() === stateName.toLowerCase()
  );
  return state?.stateCode ?? null;
}
